import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyWhatsApp from "@/components/StickyWhatsApp";
import { useLang } from "@/lib/LanguageContext";
import { LEGAL_IDENTITY, LEGAL_DISCLAIMER_EN, LEGAL_DISCLAIMER_FR } from "@/lib/pfd";

const CONTENT = {
  en: {
    seoTitle: "Legal notice · Mentions légales | Book & Smile",
    seoDescription:
      "Legal notice of Book & Smile — the commercial name of Book & Smile (EURL), registered with RCS Melun 992 697 888. Editor, hosting, personal data and applicable law.",
    eyebrow: "Compliance",
    h1: "Legal notice.",
    h1Sub: "In accordance with article 6 III of the French LCEN Act of 21 June 2004.",
    back: "Back to the house",
    disclaimer: LEGAL_DISCLAIMER_EN,
    sections: [
      {
        h2: "Editor of the website",
        lede: `${LEGAL_IDENTITY.legalName} — ${LEGAL_IDENTITY.operatorLabel} ${LEGAL_IDENTITY.operator}.`,
        rows: [
          ["Legal name", LEGAL_IDENTITY.legalName],
          ["Legal form", LEGAL_IDENTITY.legalForm],
          ["Operated by", LEGAL_IDENTITY.operator],
          ["Commercial name", LEGAL_IDENTITY.commercialName],
          ["Registered office", LEGAL_IDENTITY.headquartersAddress],
          ["Registration", `RCS ${LEGAL_IDENTITY.rcsCity} · ${LEGAL_IDENTITY.rcsNumber}`],
          ["SIREN", LEGAL_IDENTITY.siren],
          ["Registration date", LEGAL_IDENTITY.registrationDate],
          ["VAT status", LEGAL_IDENTITY.vatStatus],
          ["Publication director", LEGAL_IDENTITY.director],
          ["Contact email", LEGAL_IDENTITY.contactEmail],
          ["Contact phone / WhatsApp", LEGAL_IDENTITY.contactPhone],
        ],
      },
      {
        h2: "Website hosting",
        body:
          "This website is hosted by Emergent Labs, on Google Cloud Platform infrastructure served through Kubernetes and Cloudflare. For any hosting-related enquiry, please contact the editor at " +
          LEGAL_IDENTITY.contactEmail +
          ", who will forward the request to the hosting provider.",
      },
      {
        h2: "Nature of activity",
        body:
          "Book & Smile is a private assistance and executive support house serving international clients in Paris and across France. The house does not provide regulated legal, tax, accounting or real estate brokerage services; where regulated advice is required, licensed professionals chosen by the client are coordinated.",
      },
      {
        h2: "Intellectual property",
        body:
          "All content on this website — texts, editorial articles, illustrations, logos, page architecture, code — is the exclusive property of Book & Smile or is used with permission. Any reproduction, adaptation, translation, or communication to the public, whether in whole or in part, is prohibited without the prior written consent of the editor.",
      },
      {
        h2: "Personal data & GDPR",
        body:
          "The editor collects personal data through the contact form (name, email, telephone, message content) for the sole purpose of responding to your enquiry. The legal basis is the pre-contractual measures at the request of the person concerned (art. 6(1)(b) GDPR). Data are retained for a maximum of thirty-six months after the last exchange, and are never transferred to third parties for commercial purposes.",
      },
      {
        h2: "Your rights",
        body:
          "In accordance with the French Data Protection Act (Loi Informatique et Libertés) and the GDPR, you have a right of access, rectification, erasure, portability and objection concerning your personal data. To exercise these rights, please write to " +
          LEGAL_IDENTITY.contactEmail +
          ". You also have the right to lodge a complaint with the CNIL (www.cnil.fr).",
      },
      {
        h2: "Cookies",
        body:
          "This website uses a minimal set of cookies: (i) a technical cookie for language preference (bs_lang), retained locally and never transmitted; (ii) Google Analytics 4 for anonymous audience measurement. No advertising cookie is used.",
      },
      {
        h2: "Applicable law",
        body:
          "This legal notice is governed by French law. Any dispute relating to the interpretation or performance of these terms shall be brought before the competent French courts of the registered-office jurisdiction (Melun).",
      },
    ],
  },
  fr: {
    seoTitle: "Mentions légales | Book & Smile",
    seoDescription:
      "Mentions légales de Book & Smile (EURL, RCS Melun 992 697 888) : éditeur, hébergement, données personnelles et droit applicable.",
    eyebrow: "Conformité",
    h1: "Mentions légales.",
    h1Sub: "Conformément à l'article 6 III de la loi LCEN n° 2004-575 du 21 juin 2004.",
    back: "Retour à la maison",
    disclaimer: LEGAL_DISCLAIMER_FR,
    sections: [
      {
        h2: "Éditeur du site",
        lede: `${LEGAL_IDENTITY.legalName} — ${LEGAL_IDENTITY.operatorLabel} ${LEGAL_IDENTITY.operator}.`,
        rows: [
          ["Raison sociale", LEGAL_IDENTITY.legalName],
          ["Forme juridique", LEGAL_IDENTITY.legalForm],
          ["Exploitée par", LEGAL_IDENTITY.operator],
          ["Nom commercial", LEGAL_IDENTITY.commercialName],
          ["Siège social", LEGAL_IDENTITY.headquartersAddress],
          ["Immatriculation", `RCS ${LEGAL_IDENTITY.rcsCity} · ${LEGAL_IDENTITY.rcsNumber}`],
          ["SIREN", LEGAL_IDENTITY.siren],
          ["Date d'immatriculation", LEGAL_IDENTITY.registrationDate],
          ["Régime TVA", LEGAL_IDENTITY.vatStatus],
          ["Directeur de la publication", LEGAL_IDENTITY.director],
          ["Email de contact", LEGAL_IDENTITY.contactEmail],
          ["Téléphone / WhatsApp", LEGAL_IDENTITY.contactPhone],
        ],
      },
      {
        h2: "Hébergement du site",
        body:
          "Ce site est hébergé par Emergent Labs, sur une infrastructure Google Cloud Platform servie via Kubernetes et Cloudflare. Pour toute demande relative à l'hébergement, merci de contacter l'éditeur à l'adresse " +
          LEGAL_IDENTITY.contactEmail +
          ", qui transmettra la demande à l'hébergeur.",
      },
      {
        h2: "Nature de l'activité",
        body:
          "Book & Smile est une maison d'assistance privée et de support exécutif au service d'une clientèle internationale à Paris et en France. La maison ne fournit aucun service réglementé de conseil juridique, fiscal, comptable ou d'intermédiation immobilière ; lorsqu'un conseil réglementé est requis, les professionnels agréés choisis par le client sont coordonnés.",
      },
      {
        h2: "Propriété intellectuelle",
        body:
          "L'ensemble des contenus du site — textes, articles éditoriaux, illustrations, logos, architecture des pages, code — est la propriété exclusive de Book & Smile ou est utilisé avec autorisation. Toute reproduction, adaptation, traduction ou communication au public, totale ou partielle, est interdite sans autorisation écrite préalable de l'éditeur.",
      },
      {
        h2: "Données personnelles & RGPD",
        body:
          "L'éditeur collecte des données personnelles via le formulaire de contact (nom, email, téléphone, contenu du message) dans la seule finalité de répondre à votre demande. La base légale est l'exécution de mesures pré-contractuelles à la demande de la personne concernée (art. 6.1.b RGPD). Les données sont conservées trente-six mois maximum après le dernier échange, et ne sont jamais transférées à des tiers à des fins commerciales.",
      },
      {
        h2: "Vos droits",
        body:
          "Conformément à la loi Informatique et Libertés et au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition concernant vos données personnelles. Pour exercer ces droits, écrivez à " +
          LEGAL_IDENTITY.contactEmail +
          ". Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (www.cnil.fr).",
      },
      {
        h2: "Cookies",
        body:
          "Le site utilise un jeu de cookies minimal : (i) un cookie technique pour la préférence de langue (bs_lang), conservé localement et jamais transmis ; (ii) Google Analytics 4 pour une mesure d'audience anonymisée. Aucun cookie publicitaire n'est utilisé.",
      },
      {
        h2: "Droit applicable",
        body:
          "Les présentes mentions légales sont soumises au droit français. Tout litige relatif à l'interprétation ou à l'exécution des présentes sera porté devant les juridictions françaises compétentes du ressort du siège social (Melun).",
      },
    ],
  },
};

const MentionsLegales = () => {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.fr;

  useEffect(() => {
    document.title = c.seoTitle;
    const setMeta = (name, value) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setMeta("description", c.seoDescription);
    window.scrollTo(0, 0);
  }, [c.seoTitle, c.seoDescription]);

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="mentions-legales-page">
      <Header light={false} />
      <main>
        <section className="pt-32 sm:pt-40 pb-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <Link
              to="/"
              data-testid="mentions-legales-back"
              className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors mb-12 py-2"
            >
              <ArrowLeft size={14} /> {c.back}
            </Link>
            <p className="eyebrow text-stone-500 mb-6">{c.eyebrow}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-stone-900">
              {c.h1}
              <span className="block italic text-stone-500 mt-3 text-2xl sm:text-3xl lg:text-4xl">
                {c.h1Sub}
              </span>
            </h1>
          </div>
        </section>

        {c.sections.map((s, idx) => (
          <section
            key={s.h2}
            className={`py-14 border-t border-stone-200 ${idx === 0 ? "" : ""}`}
            data-testid={`legal-section-${idx}`}
          >
            <div className="max-w-4xl mx-auto px-6 lg:px-10">
              <h2 className="font-serif text-2xl sm:text-3xl text-stone-900 mb-6">
                {s.h2}
              </h2>
              {s.lede && (
                <p
                  data-testid="legal-editor-lede"
                  className="text-stone-800 leading-relaxed max-w-3xl mb-8 font-medium"
                >
                  {s.lede}
                </p>
              )}
              {s.rows ? (
                <dl className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-x-8 gap-y-3 text-stone-700">
                  {s.rows.map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt className="text-stone-500 text-sm">{k}</dt>
                      <dd className="text-stone-800">{v}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              ) : (
                <p className="text-stone-700 leading-relaxed max-w-3xl">
                  {s.body}
                </p>
              )}
            </div>
          </section>
        ))}

        <section className="py-14 border-t border-stone-200">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <p className="text-xs sm:text-sm text-stone-500 italic leading-relaxed max-w-3xl">
              {c.disclaimer}
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <StickyWhatsApp />
    </div>
  );
};

export default MentionsLegales;
